import { ReactNode, useMemo, useState } from "react";
import { useMap, useMapEvents } from "react-leaflet";
import { LatLngBounds } from "leaflet";
import { CupPoint, getLatLngTupleForCupPoint } from "../utils/CupPointUtils";

// merge.cup holds every openflightmaps region (~11k points); one Leaflet marker each would stall and clutter
// the map. So nothing is drawn below MIN_ZOOM (dense regions like Germany/Italy are a solid carpet of icons),
// and from MIN_ZOOM in, every point inside the view.
const MIN_ZOOM = 8;

type View = { bounds: LatLngBounds; zoom: number };

const currentView = (map: ReturnType<typeof useMap>): View | undefined => {
  try {
    return { bounds: map.getBounds().pad(0.2), zoom: map.getZoom() };
  } catch {
    return undefined; // the map has no view yet; its first moveend sets one
  }
};

interface VisibleCupPointsProps<P> {
  points: P[];
  // Called with the point and its index in `points`, so keys stay stable as the view changes.
  children: (point: P, index: number) => ReactNode;
}

function VisibleCupPoints<P extends Pick<CupPoint, "lat" | "lon" | "style">>({ points, children }: VisibleCupPointsProps<P>) {
  const map = useMap();
  const [view, setView] = useState(() => currentView(map));
  useMapEvents({ moveend: () => setView(currentView(map)) });

  const located = useMemo(() => points.map((point, index) => ({ point, index, latLng: getLatLngTupleForCupPoint(point) })), [points]);

  const visible = useMemo(() => {
    if (!view || view.zoom < MIN_ZOOM) return [];
    return located.filter((p) => view.bounds.contains(p.latLng));
  }, [located, view]);

  return <>{visible.map(({ point, index }) => children(point, index))}</>;
}

// Bottom-left notice while the map is zoomed out past MIN_ZOOM, so empty layers don't look like missing data.
// Must be rendered inside a MapContainer.
export function ZoomToSeePointsHint() {
  const map = useMap();
  const [zoom, setZoom] = useState(() => currentView(map)?.zoom);
  useMapEvents({ moveend: () => setZoom(currentView(map)?.zoom) });

  if (zoom === undefined || zoom >= MIN_ZOOM) return null;
  return (
    <div className="leaflet-bottom leaflet-left">
      <div className="leaflet-control leaflet-bar bg-white px-2 py-1 small">Zoom in to see airports and VFR points</div>
    </div>
  );
}

export default VisibleCupPoints;

// Mirrors a LayersControl overlay's checkbox into React state (overlay events fire on the map).
export function OverlayToggleSync({ name, onChange }: { name: string; onChange: (on: boolean) => void }) {
  useMapEvents({
    overlayadd(e) {
      if (e.name === name) onChange(true);
    },
    overlayremove(e) {
      if (e.name === name) onChange(false);
    },
  });
  return null;
}
